import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { CalendarClock, Plus, Trash2, ChevronDown, Copy, CheckCircle2, Send, LayoutTemplate, AlertTriangle, Repeat, X, Printer, Ban } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { ApprovalControls } from "../../components/ApprovalControls";
import { fmtDateStr, fmtTime } from "../../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

function defaultRange() {
  const today = new Date();
  const future = new Date(); future.setDate(today.getDate() + 14);
  const iso = (d) => d.toISOString().slice(0, 10);
  return [iso(today), iso(future)];
}

const STATUS_META = {
  on_time: { label: "On time", cls: "bg-emerald-500/10 text-emerald-400" },
  late: { label: "Late in", cls: "bg-amber-500/10 text-amber-400" },
  early_leave: { label: "Left early", cls: "bg-amber-500/10 text-amber-400" },
  late_early: { label: "Late & early", cls: "bg-amber-500/10 text-amber-400" },
  absent: { label: "No punch", cls: "bg-rose-500/10 text-rose-400" },
  upcoming: { label: "Upcoming", cls: "bg-slate-800 text-slate-400" },
};

function StatusBadge({ row }) {
  const m = STATUS_META[row.status] || STATUS_META.upcoming;
  return <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${m.cls}`} data-testid={`schedule-status-${row.id}`}>{m.label}</span>;
}

const to12h = (t) => { if (!t) return ""; const p = t.split(":"); let h = Number(p[0]); const ap = h < 12 ? "AM" : "PM"; h = h % 12 || 12; return `${h}:${String(p[1]).padStart(2, "0")} ${ap}`; };
const mins = (t) => { const p = t.split(":").map(Number); return p[0] * 60 + p[1]; };
const dur = (s, e) => { let m = mins(e) - mins(s); if (m < 0) m += 1440; return m / 60; };

export default function Schedules({ employees, company }) {
  const [df, dt] = defaultRange();
  const [from, setFrom] = useState(df);
  const [to, setTo] = useState(dt);
  const [rows, setRows] = useState([]);
  const [approved, setApproved] = useState(false);
  const [pending, setPending] = useState(null); // {start_time,end_time,note,source:'copy'|'template',name?}
  const [templates, setTemplates] = useState([]);
  const [swaps, setSwaps] = useState([]);
  const [history, setHistory] = useState([]);
  const [availability, setAvailability] = useState([]);
  const draggedRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [pubOpen, setPubOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ employee_id: "", date: df, start_time: "09:00", end_time: "17:00", note: "" });
  const [tplOpen, setTplOpen] = useState(false);
  const [tplForm, setTplForm] = useState({ name: "", start_time: "09:00", end_time: "17:00", note: "" });
  const [swapFor, setSwapFor] = useState(null); // schedule row we're swapping (admin)
  const [swapTarget, setSwapTarget] = useState("");
  const [confirm, setConfirm] = useState(null); // { title, message, confirmLabel, danger, onConfirm }
  const askConfirm = (opts) => setConfirm(opts);
  const runConfirm = async () => { const c = confirm; setConfirm(null); if (c?.onConfirm) await c.onConfirm(); };

  const load = useCallback(() => {
    api.get(`/schedules?from=${from}&to=${to}`).then(({ data }) => { setRows(data.rows); setApproved(data.all_approved); }).catch(() => {});
    api.get(`/swaps`).then(({ data }) => { setSwaps(data.filter((s) => s.status === "pending")); setHistory(data.filter((s) => s.status !== "pending")); }).catch(() => {});
    api.get(`/availability`).then(({ data }) => setAvailability(data)).catch(() => {});
  }, [from, to]);
  const loadTemplates = useCallback(() => { api.get("/shift-templates").then(({ data }) => setTemplates(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const openNew = () => { setEditingId(null); setForm({ employee_id: employees[0]?.id || "", date: from, start_time: "09:00", end_time: "17:00", note: "" }); setOpen(true); };

  const save = async () => {
    if (!form.employee_id) { toast.error("Choose an employee"); return; }
    setSaving(true);
    try {
      if (editingId) { await api.put(`/schedules/${editingId}`, form); toast.success("Shift updated"); }
      else { await api.post("/schedules", form); toast.success("Shift scheduled"); }
      setOpen(false); setEditingId(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const deleteEditing = async () => {
    if (!editingId) return;
    await api.delete(`/schedules/${editingId}`); toast.success("Deleted"); setOpen(false); setEditingId(null); load();
  };
  const remove = async (id) => {
    askConfirm({
      title: "Delete shift", message: "This removes the scheduled shift. This can't be undone.",
      confirmLabel: "Delete", danger: true,
      onConfirm: async () => { await api.delete(`/schedules/${id}`); toast.success("Deleted"); load(); },
    });
  };

  const saveAsTemplate = async () => {
    const name = window.prompt("Template name (e.g. Opening 9–5):");
    if (!name || !name.trim()) return;
    try {
      await api.post("/shift-templates", { name: name.trim(), start_time: form.start_time, end_time: form.end_time, note: form.note || null });
      toast.success("Template saved"); loadTemplates();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const createTemplate = async () => {
    if (!tplForm.name.trim()) { toast.error("Name your template"); return; }
    try {
      await api.post("/shift-templates", { name: tplForm.name.trim(), start_time: tplForm.start_time, end_time: tplForm.end_time, note: tplForm.note || null });
      setTplForm({ name: "", start_time: "09:00", end_time: "17:00", note: "" }); loadTemplates(); toast.success("Template added");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const deleteTemplate = async (id) => { await api.delete(`/shift-templates/${id}`); loadTemplates(); toast.success("Template removed"); };

  const grouped = rows.reduce((acc, r) => { (acc[r.employee_name] = acc[r.employee_name] || []).push(r); return acc; }, {});
  const inputCls = "mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 focus:border-blue-500 outline-none transition";

  const dayList = (() => {
    const out = []; const s = new Date(from + "T00:00:00"); const e = new Date(to + "T00:00:00");
    for (let d = new Date(s); d <= e && out.length < 31; d.setDate(d.getDate() + 1)) out.push(d.toISOString().slice(0, 10));
    return out;
  })();
  const cellMap = rows.reduce((acc, r) => { (acc[`${r.employee_id}|${r.date}`] = acc[`${r.employee_id}|${r.date}`] || []).push(r); return acc; }, {});
  const dm = (d) => { const p = d.split("-"); return `${p[1]}/${p[2]}`; };

  // ---- Coverage warnings (overnight-aware) ----
  const absInt = (r) => { const [y, m, d] = r.date.split("-").map(Number); const base = Date.UTC(y, m - 1, d) / 60000; let a = base + mins(r.start_time), b = base + mins(r.end_time); if (b <= a) b += 1440; return [a, b]; };
  const doubleBooked = new Set();     // schedule ids
  const conflictKeys = new Set();     // employee ids with a conflict
  employees.forEach((emp) => {
    const list = rows.filter((r) => r.employee_id === emp.id).map((r) => ({ id: r.id, iv: absInt(r) }));
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const a = list[i].iv, b = list[j].iv;
        if (a[0] < b[1] && b[0] < a[1]) { doubleBooked.add(list[i].id); doubleBooked.add(list[j].id); conflictKeys.add(emp.id); }
      }
    }
  });
  const uncoveredDays = dayList.filter((d) => !rows.some((r) => r.date === d));
  const WEEKLY_OT = Number(company?.overtime_weekly_threshold) || 40;
  const weekKey = (d) => { const dt = new Date(d + "T00:00:00"); const day = (dt.getDay() + 6) % 7; dt.setDate(dt.getDate() - day); return dt.toISOString().slice(0, 10); };
  const otEmployees = new Set();
  const otWarnings = [];
  employees.forEach((emp) => {
    const byWeek = {};
    rows.filter((r) => r.employee_id === emp.id).forEach((r) => { const k = weekKey(r.date); byWeek[k] = (byWeek[k] || 0) + dur(r.start_time, r.end_time); });
    Object.entries(byWeek).forEach(([wk, h]) => { if (h > WEEKLY_OT) { otEmployees.add(emp.id); otWarnings.push(`${emp.name}: ${h.toFixed(1)}h scheduled week of ${dm(wk)}`); } });
  });
  // availability conflicts (a shift landing on an employee's unavailable time)
  const wdayOf = (d) => { const [y, m, dd] = d.split("-").map(Number); return (new Date(Date.UTC(y, m - 1, dd)).getUTCDay() + 6) % 7; };
  const availConflicts = new Set();
  const availWarnings = [];
  rows.forEach((r) => {
    const wd = wdayOf(r.date);
    const hit = availability.find((a) => a.employee_id === r.employee_id
      && ((a.scope === "date" && a.date === r.date) || (a.scope === "weekday" && a.weekday === wd))
      && (a.all_day || (a.start_time && a.end_time && mins(r.start_time) < mins(a.end_time) && mins(a.start_time) < mins(r.end_time))));
    if (hit) { availConflicts.add(r.id); availWarnings.push(`${r.employee_name} on ${dm(r.date)}`); }
  });
  const hasWarnings = doubleBooked.size > 0 || uncoveredDays.length > 0 || otWarnings.length > 0 || availWarnings.length > 0;

  const openCell = (eid, date) => { setEditingId(null); setForm({ employee_id: eid, date, start_time: "09:00", end_time: "17:00", note: "" }); setOpen(true); };
  const openEditShift = (r) => { setEditingId(r.id); setForm({ employee_id: r.employee_id, date: r.date, start_time: r.start_time, end_time: r.end_time, note: r.note || "" }); setOpen(true); };
  const empTotal = (eid) => rows.filter((r) => r.employee_id === eid).reduce((t, r) => t + dur(r.start_time, r.end_time), 0);
  const grandTotal = rows.reduce((t, r) => t + dur(r.start_time, r.end_time), 0);

  const approveSchedule = async () => {
    try { const { data } = await api.post(`/schedules/approve?from=${from}&to=${to}`); toast.success(`Approved ${data.approved} shift(s)`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const openPublish = () => setPubOpen(true);
  const doPublish = async () => {
    setPubOpen(false);
    setPublishing(true);
    try {
      const { data } = await api.post(`/schedules/publish?from=${from}&to=${to}`);
      if (data.emailed > 0) toast.success(`Published ${data.published} shift(s) · emailed ${data.emailed}${data.skipped_unchanged ? ` · ${data.skipped_unchanged} unchanged (skipped)` : ""}`);
      else if (data.skipped_unchanged > 0) toast.success(`Published ${data.published} shift(s) · no changes to notify (${data.skipped_unchanged} unchanged)`);
      else toast.warning(`Published ${data.published} shift(s) · no employees had an email on file`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setPublishing(false); }
  };
  const printRoster = async () => {
    try {
      const { data } = await api.get(`/schedules/roster.pdf?from=${from}&to=${to}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch { toast.error("Could not generate the roster"); }
  };
  const applyPending = async (eid, date) => {
    try { await api.post("/schedules", { employee_id: eid, date, start_time: pending.start_time, end_time: pending.end_time, note: pending.note || "" }); toast.success("Shift added"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const dropShift = (eid, date) => {
    const id = draggedRef.current; draggedRef.current = null;
    if (!id) return;
    api.put(`/schedules/${id}`, { employee_id: eid, date }).then(() => { toast.success("Shift moved"); load(); }).catch(() => toast.error("Move failed"));
  };

  const submitSwap = async () => {
    if (!swapTarget) { toast.error("Choose a coworker"); return; }
    try {
      await api.post("/swaps", { schedule_id: swapFor.id, target_employee_id: swapTarget });
      toast.success("Swap request created"); setSwapFor(null); setSwapTarget(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const resolveSwap = async (id, action) => {
    try { await api.post(`/swaps/${id}/${action}`); toast.success(action === "approve" ? "Swap approved" : "Swap denied"); load(); }
    catch (e) {
      if (action === "approve" && e.response?.status === 409) {
        askConfirm({
          title: "Scheduling conflict", message: apiError(e.response?.data?.detail),
          confirmLabel: "Approve anyway", danger: true,
          onConfirm: async () => {
            try { await api.post(`/swaps/${id}/approve?force=true`); toast.success("Swap approved"); load(); }
            catch (er) { toast.error(apiError(er.response?.data?.detail)); }
          },
        });
        return;
      }
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div><h1 className="text-2xl font-bold">Schedules</h1><p className="text-slate-400 text-sm mt-1">Assign shifts and compare scheduled vs. actual hours</p></div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-sm">
            <input data-testid="schedule-range-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-lg bg-slate-950 border border-slate-700 px-3 py-2 focus:border-blue-500 outline-none" />
            <span className="text-slate-500">to</span>
            <input data-testid="schedule-range-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-lg bg-slate-950 border border-slate-700 px-3 py-2 focus:border-blue-500 outline-none" />
          </div>
          <button data-testid="print-roster-button" onClick={printRoster}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 font-semibold bg-slate-800 hover:bg-slate-700 transition-colors">
            <Printer className="w-4 h-4" /> Print roster
          </button>
          <button data-testid="publish-schedule-button" onClick={openPublish} disabled={publishing}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 font-semibold bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 transition-colors">
            <Send className="w-4 h-4" /> {publishing ? "Publishing…" : "Publish & notify"}
          </button>
          <ApprovalControls type="schedule" from={from} to={to} company={company} />
          <button data-testid="approve-schedule-button" onClick={approveSchedule}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 font-semibold transition-colors ${approved ? "bg-emerald-600 hover:bg-emerald-500" : "bg-slate-800 hover:bg-slate-700"}`}>
            <CheckCircle2 className="w-4 h-4" /> {approved ? "Approved" : "Approve"}
          </button>
          <button data-testid="add-schedule-button" onClick={openNew} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 rounded-full px-4 py-2.5 font-semibold transition-colors">
            <Plus className="w-4 h-4" /> Add shift
          </button>
        </div>
      </div>

      {/* Template bar */}
      <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="template-bar">
        <span className="text-xs text-slate-500 inline-flex items-center gap-1.5"><LayoutTemplate className="w-4 h-4" /> Templates:</span>
        {templates.map((t) => (
          <button key={t.id} data-testid={`template-chip-${t.id}`}
            onClick={() => setPending({ start_time: t.start_time, end_time: t.end_time, note: t.note || "", source: "template", name: t.name })}
            className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${pending?.source === "template" && pending?.name === t.name ? "bg-blue-600 border-blue-500 text-white" : "bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
            {t.name} · {to12h(t.start_time)}–{to12h(t.end_time)}
          </button>
        ))}
        {templates.length === 0 && <span className="text-xs text-slate-600">none yet</span>}
        <button data-testid="manage-templates-button" onClick={() => setTplOpen(true)} className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Manage</button>
      </div>

      {pending && (
        <div data-testid="pending-banner" className="mb-4 flex items-center justify-between bg-blue-500/10 border border-blue-500/30 text-blue-300 rounded-xl px-4 py-2 text-sm">
          <span>{pending.source === "template" ? `Template "${pending.name}"` : "Copied shift"} armed ({to12h(pending.start_time)} – {to12h(pending.end_time)}) · click any day cell to drop it.</span>
          <button data-testid="pending-cancel" onClick={() => setPending(null)} className="underline hover:text-blue-200">Cancel</button>
        </div>
      )}

      {/* Coverage warnings */}
      {hasWarnings && (
        <div data-testid="coverage-warnings" className="mb-4 bg-amber-500/10 border border-amber-500/30 text-amber-300 rounded-xl px-4 py-3 text-sm">
          <div className="flex items-center gap-2 font-semibold mb-1"><AlertTriangle className="w-4 h-4" /> Coverage warnings</div>
          <ul className="list-disc pl-5 space-y-0.5 text-amber-200/90">
            {uncoveredDays.length > 0 && <li data-testid="warning-uncovered">{uncoveredDays.length} day{uncoveredDays.length !== 1 ? "s" : ""} with no shifts scheduled ({uncoveredDays.slice(0, 6).map(dm).join(", ")}{uncoveredDays.length > 6 ? "…" : ""})</li>}
            {doubleBooked.size > 0 && <li data-testid="warning-double-booked">{conflictKeys.size} employee{conflictKeys.size !== 1 ? "s" : ""} with overlapping / double-booked shifts (overnight-aware) — highlighted in red on the calendar</li>}
            {otWarnings.length > 0 && <li data-testid="warning-overtime">Overtime — {otWarnings.join("; ")} (over {WEEKLY_OT}h/week)</li>}
            {availWarnings.length > 0 && <li data-testid="warning-availability">{availWarnings.length} shift{availWarnings.length !== 1 ? "s" : ""} on an employee's unavailable time ({availWarnings.slice(0, 6).join(", ")}{availWarnings.length > 6 ? "…" : ""}) — outlined in amber</li>}
          </ul>
        </div>
      )}

      {/* Pending swap requests */}
      {swaps.length > 0 && (
        <div className="mb-6 bg-slate-900/80 border border-slate-800 rounded-2xl overflow-hidden" data-testid="swap-requests">
          <div className="px-5 py-3 border-b border-slate-800 text-sm text-slate-300 font-semibold flex items-center gap-2"><Repeat className="w-4 h-4 text-indigo-400" /> Shift swap requests ({swaps.length})</div>
          <div className="divide-y divide-slate-800/60">
            {swaps.map((s) => (
              <div key={s.id} className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`swap-row-${s.id}`}>
                <div className="text-sm">
                  <span className="font-medium text-slate-200">{s.requester_name}</span>
                  <span className="text-slate-500"> → </span>
                  <span className="font-medium text-slate-200">{s.target_name}</span>
                  <span className="text-slate-400 ml-2 font-mono text-xs">
                    {s.schedule?.date ? `${fmtDateStr(s.schedule.date)} · ${to12h(s.schedule.start_time)}–${to12h(s.schedule.end_time)}` : "shift removed"}
                  </span>
                  {s.target_conflict && <span data-testid={`swap-conflict-${s.id}`} className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300">conflict</span>}
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">{s.source === "employee" ? "requested by staff" : "manager"}</span>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`swap-approve-${s.id}`} onClick={() => resolveSwap(s.id, "approve")} className="rounded-lg bg-emerald-600 hover:bg-emerald-500 px-3 py-1.5 text-sm font-semibold text-white transition-colors">Approve</button>
                  <button data-testid={`swap-deny-${s.id}`} onClick={() => resolveSwap(s.id, "deny")} className="rounded-lg bg-slate-800 hover:bg-slate-700 px-3 py-1.5 text-sm font-semibold transition-colors">Deny</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {history.length > 0 && (
        <details className="mb-6 bg-slate-900/80 border border-slate-800 rounded-2xl overflow-hidden" data-testid="swap-history">
          <summary className="px-5 py-3 cursor-pointer select-none list-none text-sm text-slate-300 font-semibold flex items-center gap-2"><Repeat className="w-4 h-4 text-slate-500" /> Swap history ({history.length})</summary>
          <div className="divide-y divide-slate-800/60 border-t border-slate-800">
            {history.map((s) => (
              <div key={s.id} className="px-5 py-2.5 flex items-center justify-between gap-3 flex-wrap text-sm" data-testid={`swap-history-row-${s.id}`}>
                <div>
                  <span className="text-slate-300">{s.requester_name}</span><span className="text-slate-500"> → </span><span className="text-slate-300">{s.target_name}</span>
                  <span className="text-slate-500 ml-2 font-mono text-xs">{s.schedule?.date ? `${fmtDateStr(s.schedule.date)} · ${to12h(s.schedule.start_time)}–${to12h(s.schedule.end_time)}` : "shift removed"}</span>
                </div>
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${s.status === "approved" ? "bg-emerald-500/10 text-emerald-400" : "bg-rose-500/10 text-rose-400"}`}>{s.status}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="mb-6 bg-slate-900/80 border border-slate-800 rounded-2xl overflow-hidden" data-testid="schedule-calendar">
        <div className="px-5 py-3 border-b border-slate-800 text-sm text-slate-400">Pay period {fmtDateStr(from)} – {fmtDateStr(to)} · tap a day to schedule · <span className="text-slate-300 font-medium">{grandTotal.toFixed(1)}h scheduled</span></div>
        <div className="overflow-x-auto">
          <table className="text-xs border-collapse min-w-full">
            <thead>
              <tr className="bg-slate-950/40 text-slate-400">
                <th className="sticky left-0 z-10 bg-slate-950 text-left px-3 py-2 font-semibold min-w-[140px]">Employee</th>
                {dayList.map((d) => (
                  <th key={d} data-testid={`day-header-${d}`} className={`px-2 py-2 font-mono font-medium whitespace-nowrap border-l border-slate-800/60 ${uncoveredDays.includes(d) ? "text-amber-400" : ""}`}>
                    {dm(d)}{uncoveredDays.includes(d) && <span title="No coverage"> ⚠</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => (
                <tr key={emp.id} className="border-t border-slate-800/60" data-testid={`calendar-row-${emp.id}`}>
                  <td className="sticky left-0 z-10 bg-slate-900 px-3 py-2 font-medium whitespace-nowrap">
                    {emp.name}
                    <div className={`text-[10px] font-mono ${otEmployees.has(emp.id) ? "text-amber-400" : "text-slate-500"}`} data-testid={`emp-total-${emp.id}`}>{empTotal(emp.id).toFixed(1)}h{otEmployees.has(emp.id) && " ⚠"}</div>
                  </td>
                  {dayList.map((d) => {
                    const cell = cellMap[`${emp.id}|${d}`] || [];
                    return (
                      <td key={d} data-testid={`calendar-cell-${emp.id}-${d}`}
                        onClick={() => (pending ? applyPending(emp.id, d) : openCell(emp.id, d))}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => { e.preventDefault(); dropShift(emp.id, d); }}
                        className="align-top px-1.5 py-1.5 border-l border-slate-800/40 cursor-pointer hover:bg-slate-800/40 transition-colors min-w-[92px]">
                        {cell.map((r) => {
                          const conflict = doubleBooked.has(r.id);
                          const unavail = availConflicts.has(r.id);
                          return (
                            <div key={r.id} draggable data-testid={`shift-chip-${r.id}`}
                              onDragStart={(e) => { draggedRef.current = r.id; e.dataTransfer.effectAllowed = "move"; }}
                              onClick={(e) => { e.stopPropagation(); openEditShift(r); }}
                              className={`mb-1 rounded-md px-1.5 py-1 text-[10px] font-mono flex items-center justify-between gap-1 cursor-pointer ${conflict ? "bg-rose-500/15 text-rose-300 ring-1 ring-rose-500/50" : unavail ? "bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/50" : (STATUS_META[r.status] || STATUS_META.upcoming).cls}`}>
                              <span className="flex items-center gap-1">{conflict && <AlertTriangle className="w-2.5 h-2.5" />}{unavail && !conflict && <Ban className="w-2.5 h-2.5" />}{to12h(r.start_time)}–{to12h(r.end_time)}</span>
                              <span className="flex items-center gap-1">
                                <button data-testid={`swap-shift-${r.id}`} title="Swap" onClick={(e) => { e.stopPropagation(); setSwapFor(r); setSwapTarget(""); }} className="opacity-60 hover:opacity-100"><Repeat className="w-3 h-3" /></button>
                                <button data-testid={`copy-shift-${r.id}`} title="Copy" onClick={(e) => { e.stopPropagation(); setPending({ start_time: r.start_time, end_time: r.end_time, note: r.note || "", source: "copy" }); toast.success("Shift copied"); }} className="opacity-60 hover:opacity-100"><Copy className="w-3 h-3" /></button>
                              </span>
                            </div>
                          );
                        })}
                        {cell.length === 0 && <div className="text-slate-700 text-center leading-none">+</div>}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {employees.length === 0 && <tr><td className="px-3 py-6 text-slate-500" colSpan={dayList.length + 1}>No employees yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-3" data-testid="schedules-groups">
        {Object.entries(grouped).map(([name, list]) => (
          <details key={name} data-testid={`schedule-group-${name}`} open className="bg-slate-900/80 border border-slate-800 rounded-2xl overflow-hidden">
            <summary className="flex items-center justify-between px-5 py-3 cursor-pointer select-none list-none">
              <span className="font-semibold flex items-center gap-2"><ChevronDown className="w-4 h-4 text-slate-500" /> {name}</span>
              <span className="text-xs font-mono text-slate-400">{list.length} shift{list.length !== 1 ? "s" : ""}</span>
            </summary>
            <table className="w-full text-sm border-t border-slate-800">
              <thead className="bg-slate-950/40 text-slate-400 text-xs uppercase tracking-wider">
                <tr><th className="text-left px-5 py-2">Date</th><th className="text-left px-5 py-2">Scheduled</th><th className="text-left px-5 py-2">Actual</th><th className="text-left px-5 py-2">Status</th><th className="text-right px-5 py-2">Actions</th></tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id} className="border-t border-slate-800/60" data-testid={`schedule-row-${r.id}`}>
                    <td className="px-5 py-2.5 font-mono text-slate-300">{fmtDateStr(r.date)}</td>
                    <td className="px-5 py-2.5 font-mono text-slate-300">{to12h(r.start_time)} – {to12h(r.end_time)}</td>
                    <td className="px-5 py-2.5 font-mono text-slate-300">
                      {r.actual_in ? `${fmtTime(r.actual_in)} – ${r.actual_out ? fmtTime(r.actual_out) : "active"}` : <span className="text-slate-600">—</span>}
                      {r.late_minutes > 0 && <span className="ml-2 text-[10px] text-amber-400">+{r.late_minutes}m late</span>}
                      {r.early_leave_minutes > 0 && <span className="ml-2 text-[10px] text-amber-400">-{r.early_leave_minutes}m early</span>}
                    </td>
                    <td className="px-5 py-2.5"><StatusBadge row={r} /></td>
                    <td className="px-5 py-2.5 text-right">
                      <button data-testid={`delete-schedule-${r.id}`} onClick={() => remove(r.id)} className="p-2 text-slate-400 hover:text-rose-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        ))}
        {rows.length === 0 && <div className="bg-slate-900/80 border border-slate-800 rounded-2xl px-5 py-10 text-center text-slate-500 flex flex-col items-center gap-2"><CalendarClock className="w-8 h-8 text-slate-600" />No shifts scheduled in this range.</div>}
      </div>

      {/* Add / edit shift dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader><DialogTitle>{editingId ? "Edit shift" : "Schedule a shift"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium text-slate-300">Employee</label>
              <select data-testid="schedule-employee-select" value={form.employee_id} onChange={(e) => setForm((f) => ({ ...f, employee_id: e.target.value }))} className={inputCls}>
                <option value="">Select employee…</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Date</label>
              <input data-testid="schedule-date-input" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-300">Start</label>
                <input data-testid="schedule-start-input" type="time" value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-300">End</label>
                <input data-testid="schedule-end-input" type="time" value={form.end_time} onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))} className={inputCls} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Note (optional)</label>
              <input data-testid="schedule-note-input" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="e.g. Opening shift" className={inputCls} />
            </div>
            <button data-testid="save-as-template-button" onClick={saveAsTemplate} className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"><LayoutTemplate className="w-3 h-3" /> Save these times as a template</button>
          </div>
          <DialogFooter>
            <div className="flex gap-2 w-full">
              {editingId && <button data-testid="delete-editing-shift" onClick={deleteEditing} className="rounded-xl bg-rose-600/90 hover:bg-rose-500 px-4 py-2.5 font-semibold text-white transition-colors"><Trash2 className="w-4 h-4" /></button>}
              <button data-testid="save-schedule-button" onClick={save} disabled={saving} className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-500 py-2.5 font-semibold disabled:opacity-60 transition-colors">{saving ? "Saving…" : editingId ? "Save changes" : "Schedule shift"}</button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage templates dialog */}
      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader><DialogTitle>Shift templates</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2 max-h-52 overflow-y-auto" data-testid="template-list">
            {templates.map((t) => (
              <div key={t.id} className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-xl px-3 py-2" data-testid={`template-item-${t.id}`}>
                <span className="text-sm">{t.name} <span className="text-slate-500 font-mono text-xs">{to12h(t.start_time)}–{to12h(t.end_time)}</span></span>
                <button data-testid={`delete-template-${t.id}`} onClick={() => deleteTemplate(t.id)} className="p-1.5 text-slate-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {templates.length === 0 && <p className="text-sm text-slate-500 text-center py-3">No templates yet.</p>}
          </div>
          <div className="border-t border-slate-800 pt-3 space-y-3">
            <input data-testid="template-name-input" value={tplForm.name} onChange={(e) => setTplForm((f) => ({ ...f, name: e.target.value }))} placeholder="Template name (e.g. Opening 9–5)" className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="template-start-input" type="time" value={tplForm.start_time} onChange={(e) => setTplForm((f) => ({ ...f, start_time: e.target.value }))} className={inputCls} />
              <input data-testid="template-end-input" type="time" value={tplForm.end_time} onChange={(e) => setTplForm((f) => ({ ...f, end_time: e.target.value }))} className={inputCls} />
            </div>
            <button data-testid="create-template-button" onClick={createTemplate} className="w-full rounded-xl bg-blue-600 hover:bg-blue-500 py-2.5 font-semibold transition-colors">Add template</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin swap dialog */}
      <Dialog open={!!swapFor} onOpenChange={(o) => { if (!o) { setSwapFor(null); setSwapTarget(""); } }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader><DialogTitle>Request shift swap</DialogTitle></DialogHeader>
          {swapFor && (
            <div className="space-y-3 py-2">
              <p className="text-sm text-slate-400">Reassign <span className="text-slate-200 font-medium">{swapFor.employee_name}</span>'s shift on {fmtDateStr(swapFor.date)} ({to12h(swapFor.start_time)}–{to12h(swapFor.end_time)}) to another employee. It'll appear as a pending swap for your approval.</p>
              <div>
                <label className="text-sm font-medium text-slate-300">Swap with</label>
                <select data-testid="swap-target-select" value={swapTarget} onChange={(e) => setSwapTarget(e.target.value)} className={inputCls}>
                  <option value="">Select employee…</option>
                  {employees.filter((e) => e.id !== swapFor.employee_id).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <button data-testid="submit-swap-button" onClick={submitSwap} className="w-full rounded-xl bg-indigo-600 hover:bg-indigo-500 py-2.5 font-semibold transition-colors">Create swap request</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish confirm dialog */}
      <Dialog open={pubOpen} onOpenChange={setPubOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="publish-confirm-dialog">
          <DialogHeader><DialogTitle>Publish & notify</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2 text-sm text-slate-300">
            <p>This publishes the schedule for {fmtDateStr(from)} – {fmtDateStr(to)} and emails each employee (who has an email on file) their upcoming shifts.</p>
            {rows.some((r) => r.published) && (
              <p data-testid="publish-republish-warning" className="text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2">
                Some shifts in this range were already published — publishing again will re-send emails and may create duplicates.
              </p>
            )}
          </div>
          <DialogFooter>
            <div className="flex gap-2 w-full">
              <button data-testid="publish-cancel-button" onClick={() => setPubOpen(false)} className="flex-1 rounded-xl bg-slate-800 hover:bg-slate-700 py-2.5 font-semibold transition-colors">Cancel</button>
              <button data-testid="publish-confirm-button" onClick={doPublish} className="flex-1 rounded-xl bg-indigo-600 hover:bg-indigo-500 py-2.5 font-semibold transition-colors">Publish & send</button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Reusable styled confirm dialog */}
      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="confirm-dialog">
          <DialogHeader><DialogTitle>{confirm?.title}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-300 py-2">{confirm?.message}</p>
          <DialogFooter>
            <div className="flex gap-2 w-full">
              <button data-testid="confirm-cancel-button" onClick={() => setConfirm(null)} className="flex-1 rounded-xl bg-slate-800 hover:bg-slate-700 py-2.5 font-semibold transition-colors">Cancel</button>
              <button data-testid="confirm-ok-button" onClick={runConfirm} className={`flex-1 rounded-xl py-2.5 font-semibold text-white transition-colors ${confirm?.danger ? "bg-rose-600 hover:bg-rose-500" : "bg-blue-600 hover:bg-blue-500"}`}>{confirm?.confirmLabel || "Confirm"}</button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
